import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import { Box, Grid, Checkbox, Button } from "@mui/material";
import {
  DataGrid,
  GridToolbarQuickFilter,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import PropTypes from "prop-types";
import { IconButton } from "@mui/material";
import useHooks from "../hooks/useHooks";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditSettings from "./EditSettings";
import ReportingController from "./ReportingController.js";
import StatusBar from "./StatusBar.js";
import { ReportContextProvider } from "../hooks/useReportContext";

import CardLoader from "../components/CardLoader";
import ErrorBoundary from "../components/ErrorBoundary.js";

const StyledDataGridRoot = styled(Box)({
  width: "99%",
  marginLeft: 10,
  "& .MuiDataGrid-main": {
    backgroundColor: "#fff",
  },
  "& .MuiDataGrid-columnHeader.theme-header, .MuiDataGrid-columnHeaderCheckbox":
    {
      backgroundColor: "#F5F5F5",
    },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: "bold",
  },
  "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
    outline: "none !important",
  },
  "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
    {
      outline: "none !important",
    },
  "& .MuiDataGrid-cellContent": {
    fontSize: "0.75rem",
  },
});

const PageContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "calc(100vh - 60px - 95px)",
  width: "100%",
  overflow: "hidden",
});

const ContentContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  height: "100%",
  position: "relative",
  overflow: "hidden",
});

const ControllerContainer = styled(Box)({
  flexShrink: 0,
  borderRadius: "4px",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
  position: "sticky",
  top: 0,
  zIndex: 10,
  backgroundColor: "#fff",
});

const GridContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: "300px",
  height: "auto",
  position: "relative",
  overflow: "auto",
});

const StatusBarContainer = styled(Box)({
  flexShrink: 0,
  marginTop: "8px",
  position: "sticky",
  bottom: 0,
  zIndex: 10,
  backgroundColor: "#fff",
});

const FileTypeLabel = styled(Box)({
  borderRadius: "8px",
  backgroundColor: "#e0f7fa",
  padding: "8px 16px 8px 16px",
  textAlign: "center",
  fontSize: "0.75rem",
});

const StyledDataGrid = styled(DataGrid)({
  height: "100%",
  minHeight: "300px",
  flexGrow: 1,
  width: "100%",
  "& .MuiDataGrid-main": {
    flexGrow: 1,
    flexShrink: 1,
    overflow: "hidden",
    backgroundColor: "#fff",
  },
  "& .MuiDataGrid-virtualScroller": {
    overflow: "auto",
  },
  "& .MuiDataGrid-row": {
    minHeight: "40px !important",
  },
  "& .MuiDataGrid-cell": {
    padding: "0 16px",
  },
  "& .MuiDataGrid-toolbarContainer": {
    position: "sticky",
    top: 0,
    zIndex: 9,
    backgroundColor: "#fff",
    padding: "4px 0",
  },
  "& .MuiDataGrid-columnHeaders, & .css-w2aot-MultiDatraGrid-root.MuiDataGrid-columnHeaders":
    {
      position: "sticky",
      top: "0px",
      zIndex: 9,
      backgroundColor: "#F5F5F5",
    },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid rgba(224, 224, 224, 1)",
    position: "sticky",
    bottom: 0,
    backgroundColor: "#fff",
    zIndex: 9,
    padding: "0 8px",
    minHeight: "45px",
  },
  "& .MuiTablePagination-root": {
    display: "flex",
    alignItems: "center",
    margin: 0,
    padding: 0,
  },
  "& .MuiTablePagination-toolbar": {
    minHeight: "45px",
    padding: "0 8px",
  },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    margin: 0,
  },
});

const ToolbarContainer = styled(Box)({
  padding: "8px",
  paddingBottom: "4px",
});

const ToolbarLeftSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

const ToolbarRightSection = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  paddingRight: "8px",
});

const AddButton = styled(Button)({
  textTransform: "none",
  fontWeight: 500,
  borderRadius: 4,
});

const RenderActions = (props) => {
  const { onEdit } = props;
  const handleEditClick = () => {
    onEdit(props.row);
  };
  return (
    <>
      <IconButton onClick={handleEditClick} title="Edit Report Setting">
        <EditIcon />
      </IconButton>
    </>
  );
};

const AddHandlerContext = React.createContext(null);

function GridToolbar({ setFilterButtonEl }) {
  const handleAdd = React.useContext(AddHandlerContext);

  return (
    <Grid container>
      <Grid item sm={12}>
        <ToolbarContainer display="flex" justifyContent="space-between">
          <ToolbarLeftSection>
            <GridToolbarFilterButton color="info" ref={setFilterButtonEl} />
            <GridToolbarColumnsButton color="info" />
            <GridToolbarQuickFilter
              quickFilterParser={(searchInput) =>
                searchInput
                  .split(",")
                  .map((value) => value.trim())
                  .filter((value) => value !== "")
              }
            />
          </ToolbarLeftSection>
          <ToolbarRightSection>
            <Tooltip title="Add New Report Setting" arrow>
              <AddButton
                onClick={handleAdd}
                color="primary"
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
              >
                Add Compartment
              </AddButton>
            </Tooltip>
          </ToolbarRightSection>
        </ToolbarContainer>
      </Grid>
    </Grid>
  );
}
GridToolbar.propTypes = {
  setFilterButtonEl: PropTypes.func.isRequired,
};

const ReportAdmin = () => {
  const renderCellWithToolTip = React.useCallback(
    (params) => (
      <Tooltip title={params.value ? params.value.toString() : ""}>
        <span style={{ fontSize: "0.75rem" }}>{params.value}</span>
      </Tooltip>
    ),
    []
  );

  const columns = [
    {
      field: "PRODUCTNAME",
      headerName: "Compartment",
      minWidth: 210,
      flex: 3,
      editable: false,
      disableColumnMenu: true,
      filterable: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
      hideable: false,
    },
    {
      field: "ISIN",
      headerName: "ISIN",
      minWidth: 120,
      flex: 2,
      editable: false,
      disableColumnMenu: true,
      filterable: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
      hideable: true,
      hide: true,
    },
    {
      field: "REPORT_NAME_SUFFIX",
      headerName: "Report Name Suffix",
      minWidth: 200,
      flex: 3,
      editable: false,
      disableColumnMenu: true,
      filterable: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
      hideable: true,
      hide: true,
    },
    {
      field: "INCLUDE",
      headerName: "Active",
      minWidth: 80,
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
      renderCell: (params) => {
        return <Checkbox checked={params.value || false} disabled />;
      },
    },
    {
      field: "Official",
      headerName: "Official",
      minWidth: 80,
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
      valueGetter: (params) => params.row.RUN_PROFILE,
      renderCell: (params) => {
        return (
          <Checkbox checked={params.value?.includes("Official")} disabled />
        );
      },
    },
    {
      field: "PreRun",
      headerName: "PreRun",
      minWidth: 80,
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
      valueGetter: (params) => params.row.RUN_PROFILE,
      renderCell: (params) => {
        return <Checkbox checked={params.value?.includes("PreRun")} disabled />;
      },
    },
    {
      field: "SEND_EMAIL",
      headerName: "Email",
      minWidth: 80,
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
      renderCell: (params) => {
        return <Checkbox checked={params.value || false} disabled />;
      },
    },
    {
      field: "POSITIONS_OPTION",
      headerName: "Pos.",
      minWidth: 70,
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      filterable: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
      hideable: true,
      hide: true,
    },
    {
      field: "FREQUENCY_OPTION",
      headerName: "Sched.",
      minWidth: 70,
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
    },
    {
      field: "EMAIL_FORMAT",
      headerName: "Format",
      minWidth: 100,
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
      renderCell: (params) => <FileTypeLabel>{params.value}</FileTypeLabel>,
    },
    {
      field: "HIDE_NON_ACCUMULATING",
      headerName: "Hide Fees",
      minWidth: 90,
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
      renderCell: (params) => {
        return <Checkbox checked={params.value || false} disabled />;
      },
      hideable: true,
      hide: true,
    },
    {
      field: "INCLUDE_UNSETTLED_POSITIONS",
      headerName: "Include Unsettled Positions",
      minWidth: 90,
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
      renderCell: (params) => {
        return <Checkbox checked={params.value || false} disabled />;
      },
    },
    {
      field: "HISTORY",
      headerName: "History",
      minWidth: 80,
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
      type: "number",
    },
    {
      field: "EMAIL_RECIPIENTS",
      headerName: "Recipients",
      minWidth: 210,
      flex: 4,
      editable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      headerClassName: "theme-header",
      renderCell: renderCellWithToolTip,
    },
    {
      field: "SEND_FTP",
      headerName: "FTP",
      minWidth: 90,
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
    },
    {
      field: "FTP_FORMAT",
      headerName: "Format",
      minWidth: 100,
      flex: 1,
      editable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
      renderCell: (params) => <FileTypeLabel>{params.value}</FileTypeLabel>,
    },
    {
      field: "UPDATED",
      headerName: "Updated",
      minWidth: 95,
      flex: 2,
      editable: false,
      disableColumnMenu: true,
      filterable: false,
      headerAlign: "center",
      align: "center",
      type: "datetime",
      headerClassName: "theme-header",
    },
    {
      field: "UPDATEDBY",
      headerName: "Updated By",
      minWidth: 100,
      flex: 2,
      editable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      headerClassName: "theme-header",
    },
    {
      field: "ID",
      headerName: "Edit",
      minWidth: 30,
      renderCell: (props) => <RenderActions {...props} onEdit={handleEdit} />,
      sortable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      hideable: false,
      type: "number",
      headerClassName: "theme-header",
      getApplyQuickFilterFn: undefined,
    },
  ];
  const boxRef = useRef(null);

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [rowData, setRowData] = React.useState(null);
  const [editMode, setEditMode] = React.useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState([]);
  const [originalEmailRecipients, setOriginalEmailRecipients] = useState("");

  const { useReportSettings } = useHooks();
  const { loading, data: ReportSettingsData } = useReportSettings();

  let isLoading = loading;

  const [filterButtonEl, setFilterButtonEl] = React.useState(null);

  useEffect(() => {
    // Avoiding unnecessary updates by comparing new and old selected row data
    const newSelectedRowData =
      ReportSettingsData && Array.isArray(ReportSettingsData)
        ? ReportSettingsData.filter((row) => selectedRows.includes(row.ID))
        : [];

    if (
      JSON.stringify(newSelectedRowData) !== JSON.stringify(selectedRowData)
    ) {
      setSelectedRowData(newSelectedRowData);
    }
  }, [selectedRows, ReportSettingsData, selectedRowData]);

  const handleSelectionModelChange = (newSelectionModel) => {
    setSelectedRows(newSelectionModel);
  };

  const handleEdit = (rowData) => {
    setRowData(rowData);
    setOriginalEmailRecipients(rowData.EMAIL_RECIPIENTS || "");
    setDrawerOpen(true);
    setEditMode(true);
  };

  const handleAdd = () => {
    setRowData({
      RUN_PROFILE: "Official",
      EMAIL_FORMAT: "XLS",
      FTP_FORMAT: "XLS",
      FREQUENCY_OPTION: "Daily",
      POSITIONS_OPTION: "Combined",
      HISTORY: 500,
    }); // default values
    setOriginalEmailRecipients("");
    setDrawerOpen(true);
    setEditMode(false);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  return isLoading ? (
    <CardLoader />
  ) : (
    <AddHandlerContext.Provider value={handleAdd}>
      <ReportContextProvider>
        <PageContainer>
          <ContentContainer>
            <ControllerContainer>
              <ReportingController
                selectedRowData={selectedRowData}
                gridBoxRef={boxRef}
              />
            </ControllerContainer>

            <GridContainer ref={boxRef}>
              <ErrorBoundary>
                <StyledDataGridRoot>
                  <StyledDataGrid
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 100, page: 0 },
                      },
                      columns: {
                        columnVisibilityModel: {
                          ISIN: false,
                          REPORT_NAME_SUFFIX: false,
                          POSITIONS_OPTION: false,
                          INCLUDE_UNSETTLED_POSITIONS: false,
                        },
                      },
                    }}
                    rowHeight={40}
                    columnBuffer={35}
                    getRowId={(row) => row.ID}
                    rows={ReportSettingsData || []}
                    columns={columns}
                    checkboxSelection
                    disableRowSelectionOnClick
                    onRowSelectionModelChange={handleSelectionModelChange}
                    pageSizeOptions={[25, 50, 100]}
                    pagination={true}
                    slots={{
                      toolbar: GridToolbar,
                    }}
                    slotProps={{
                      panel: {
                        anchorEl: filterButtonEl,
                      },
                      toolbar: {
                        setFilterButtonEl,
                      },
                      columnsPanel: {
                        sx: {
                          "& .MuiDataGrid-panelFooter": {
                            display: "none",
                          },
                          "& .MuiSwitch-switchBase": {
                            position: "absolute",
                          },
                        },
                      },
                    }}
                  />
                </StyledDataGridRoot>
              </ErrorBoundary>
            </GridContainer>

            <StatusBarContainer>
              <StatusBar />
            </StatusBarContainer>
          </ContentContainer>

          <EditSettings
            open={drawerOpen}
            onClose={handleCloseDrawer}
            rowData={rowData}
            editMode={editMode}
            originalEmailRecipients={originalEmailRecipients}
          />
        </PageContainer>
      </ReportContextProvider>
    </AddHandlerContext.Provider>
  );
};

export default ReportAdmin;
